import type { TierGaugeTier } from "@/components/charts/TierGauge"
import type { SubvalueByTimestamp } from "@/types/RunMetricOutput"

// Provisional mismatch-rate bands (percent of classifiable sentences). Higher is worse:
// a high rate means frequent uptalk or flat yes/no questions.
// Calibrate against real reviewer data before treating as fixed.
export const NATURAL_MAX_PCT = 15
export const ELEVATED_MAX_PCT = 30
export const GAUGE_MAX_PCT = 50

// Mirrors the backend hysteresis band (FLAT_DELTA_SEMITONES): deltas within ±this
// read as flat. Drawn as guide lines on the per-turn delta timeline.
export const FLAT_BAND_ST = 1.5

// rgb triplet for mismatched-turn highlights, shared by the timeline and the
// audio-player regions so both surfaces stay in sync (alpha applied at render).
export const MISMATCH_FILL = "239, 68, 68" // red-500

export function terminalTier(mismatchPct: number): TierGaugeTier {
  if (mismatchPct <= NATURAL_MAX_PCT) {
    return {
      label: "Natural",
      variant: "success",
      blurb:
        "Sentence-final pitch fits the sentence type on nearly every sentence.",
    }
  }
  if (mismatchPct <= ELEVATED_MAX_PCT) {
    return {
      label: "Some mismatch",
      variant: "warning",
      blurb: "Some uptalk or flat questions — delivery occasionally reads off.",
    }
  }
  return {
    label: "Frequent mismatch",
    variant: "danger",
    blurb: "Frequent uptalk or flat questions — robotic or uncertain delivery.",
  }
}

// One classified agent turn from the backend's per-turn subvalues.
interface ContourTurn {
  start: number
  end: number
  deltaSt: number
  direction: string
  sentenceType: string
  isMismatch: boolean
}

const DIRECTION_LABELS: Record<string, string> = {
  rise: "rising",
  fall: "falling",
  flat: "flat",
}

const SENTENCE_LABELS: Record<string, string> = {
  statement: "statement",
  yes_no_question: "yes/no question",
  wh_question: "wh-question",
}

export function directionLabel(direction: string): string {
  return DIRECTION_LABELS[direction] ?? direction
}

export function sentenceLabel(sentenceType: string): string {
  return SENTENCE_LABELS[sentenceType] ?? sentenceType
}

// Where a flagged turn's boundary tone should have landed (semitones), drawn as a
// target arrow: yes/no questions should rise, statements should not (fall). Null
// when the sentence type carries no boundary-tone expectation.
const EXPECTED_TONE_ST = 3

export function expectedTone(
  sentenceType: string,
): { glyph: string; targetSt: number } | null {
  if (sentenceType === "yes_no_question") {
    return { glyph: "↑", targetSt: EXPECTED_TONE_ST }
  }
  if (sentenceType === "statement") {
    return { glyph: "↓", targetSt: -EXPECTED_TONE_ST }
  }
  return null
}

// string_value is "{direction}/{sentence_type}" (e.g. "rise/statement"); the
// mismatch flag lives in metadata.is_mismatch.
export function contourTurns(subvalues: SubvalueByTimestamp[]): ContourTurn[] {
  return subvalues
    .filter(
      (s) =>
        s.float_value != null &&
        typeof s.string_value === "string" &&
        s.string_value.includes("/"),
    )
    .map((s) => {
      const [direction, sentenceType] = (s.string_value as string).split("/")
      return {
        start: s.start_offset,
        end: s.end_offset,
        deltaSt: s.float_value as number,
        direction,
        sentenceType,
        isMismatch:
          (s.metadata as { is_mismatch?: boolean })?.is_mismatch === true,
      }
    })
    .sort((a, b) => a.start - b.start)
}

interface MismatchBreakdown {
  classified: number
  mismatched: number
  uptalk: number // statement that rises
  flatQuestion: number // yes/no question that stays flat
}

// Derive the breakdown from the per-turn subvalues — the backend leaves
// raw_values empty since these counts follow from string_value + is_mismatch.
export function mismatchBreakdown(
  subvalues: SubvalueByTimestamp[],
): MismatchBreakdown {
  const turns = contourTurns(subvalues)
  return {
    classified: turns.length,
    mismatched: turns.filter((t) => t.isMismatch).length,
    uptalk: turns.filter(
      (t) => t.direction === "rise" && t.sentenceType === "statement",
    ).length,
    flatQuestion: turns.filter(
      (t) => t.direction === "flat" && t.sentenceType === "yes_no_question",
    ).length,
  }
}
